import axios from 'axios';
import api from './api';
import { Dispatch } from 'redux';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { addNewsletter } from './newsletterReducer';
import { setNotification } from './notificationsReducer'; // Import the notification action creator

// Action types
const LOGIN_REQUEST = 'LOGIN_REQUEST';
import { clearActingBackups } from "@/app/lib/clearActingBackups";
const LOGIN_SUCCESS = 'LOGIN_SUCCESS';
const LOGIN_FAILURE = 'LOGIN_FAILURE';
const LOGIN_2FA_REQUIRED = 'LOGIN_2FA_REQUIRED';
const LOGIN_RESET = 'LOGIN_RESET';
const LOGOUT = 'LOGOUT';
const VERIFY_TOKEN_REQUEST = 'VERIFY_TOKEN_REQUEST';
const VERIFY_TOKEN_SUCCESS = 'VERIFY_TOKEN_SUCCESS';
const VERIFY_TOKEN_FAILURE = 'VERIFY_TOKEN_FAILURE';
const REGISTER_REQUEST = 'REGISTER_REQUEST';
const REGISTER_SUCCESS = 'REGISTER_SUCCESS';
const REGISTER_FAILURE = 'REGISTER_FAILURE';
const CREATE_RECRUITMENT_SUCCESS = 'CREATE_RECRUITMENT_SUCCESS';
const CREATE_RECRUITMENT_FAILURE = 'CREATE_RECRUITMENT_FAILURE';
const RESET_PASSWORD_REQUEST = 'RESET_PASSWORD_REQUEST';
const RESET_PASSWORD_SUCCESS = 'RESET_PASSWORD_SUCCESS';
const RESET_PASSWORD_FAILURE = 'RESET_PASSWORD_FAILURE';
const REMOVE_AUTH_ERROR = 'REMOVE_AUTH_ERROR';

const RESET_LOST_PASSWORD_REQUEST = 'RESET_LOST_PASSWORD_REQUEST';
const RESET_LOST_PASSWORD_SUCCESS = 'RESET_LOST_PASSWORD_SUCCESS';
const RESET_LOST_PASSWORD_FAILURE = 'RESET_LOST_PASSWORD_FAILURE';

const UPDATE_MY_ACCOUNT_REQUEST = 'UPDATE_MY_ACCOUNT_REQUEST';
const UPDATE_MY_ACCOUNT_SUCCESS = 'UPDATE_MY_ACCOUNT_SUCCESS';
const UPDATE_MY_ACCOUNT_FAILURE = 'UPDATE_MY_ACCOUNT_FAILURE';

// Additional action types for recruitment
interface CreateRecruitmentSuccessAction {
  type: typeof CREATE_RECRUITMENT_SUCCESS;
  payload: any; // Adjust according to the actual data structure
}

interface CreateRecruitmentFailureAction {
  type: typeof CREATE_RECRUITMENT_FAILURE;
  payload: string;
}

type RecruitmentAction = CreateRecruitmentSuccessAction | CreateRecruitmentFailureAction;

type AuthAction =
  | { type: typeof LOGIN_REQUEST }
  | { type: typeof LOGIN_SUCCESS; userData: any } // Replace 'any' with your user data type
  | { type: typeof LOGIN_FAILURE; error: string }
  | { type: typeof LOGIN_2FA_REQUIRED; payload: { twoFactorToken: string; pendingUser: any } }
  | { type: typeof LOGIN_RESET }
  | { type: typeof LOGOUT }
  | { type: typeof VERIFY_TOKEN_REQUEST }
  | { type: typeof VERIFY_TOKEN_SUCCESS; userData: any } // Same note about 'any' type
  | { type: typeof VERIFY_TOKEN_FAILURE; error: string }
  | { type: typeof REGISTER_REQUEST }
  | { type: typeof REGISTER_SUCCESS; userData: any }
  | { type: typeof REGISTER_FAILURE; error: string }
  | { type: typeof RESET_PASSWORD_REQUEST }
  | { type: typeof RESET_PASSWORD_SUCCESS }
  | { type: typeof RESET_PASSWORD_FAILURE; error: string }
  | { type: typeof REMOVE_AUTH_ERROR}
  | { type: typeof RESET_LOST_PASSWORD_REQUEST }
  | { type: typeof RESET_LOST_PASSWORD_SUCCESS }
  | { type: typeof RESET_LOST_PASSWORD_FAILURE; error: string }
  | { type: typeof UPDATE_MY_ACCOUNT_REQUEST }
  | { type: typeof UPDATE_MY_ACCOUNT_SUCCESS; userData: any }
  | { type: typeof UPDATE_MY_ACCOUNT_FAILURE; error: string }
  | RecruitmentAction; // Include RecruitmentAction in AuthAction


// Action creators
export const loginRequest = (): AuthAction => ({ type: LOGIN_REQUEST });
export const loginSuccess = (userData: any): AuthAction => ({type: LOGIN_SUCCESS, userData});
export const loginFailure = (error: string): AuthAction => ({type: LOGIN_FAILURE, error});

export const verifyTokenRequest = (): AuthAction => ({type: VERIFY_TOKEN_REQUEST});
export const verifyTokenSuccess = (userData: any): AuthAction => ({type: VERIFY_TOKEN_SUCCESS, userData });
export const verifyTokenFailure = (error: string): AuthAction => ({type: VERIFY_TOKEN_FAILURE, error});

export const registerRequest = (): AuthAction => ({ type: REGISTER_REQUEST });
export const registerSuccess = (userData: any): AuthAction => ({ type: REGISTER_SUCCESS, userData });
export const registerFailure = (error: string): AuthAction => ({ type: REGISTER_FAILURE, error });

export const resetPasswordRequest = (): AuthAction => ({ type: RESET_PASSWORD_REQUEST });
export const resetPasswordSuccess = (): AuthAction => ({ type: RESET_PASSWORD_SUCCESS });
export const resetPasswordFailure = (error: string): AuthAction => ({ type: RESET_PASSWORD_FAILURE, error });

export const removeAuthError = (): AuthAction => ({ type: REMOVE_AUTH_ERROR });

// Clear the session in place: wipe tokens from localStorage and null out
// `auth.user` in Redux — WITHOUT the hard redirect that AvatarDropdown's
// logout does. Used e.g. when an authenticated user lands on the guide
// signup form (which always creates a brand-new account), so we start
// from a clean, logged-out state without leaving the page.
export const logout = () => (dispatch: Dispatch<AuthAction>) => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    clearActingBackups();
  }
  dispatch({ type: LOGOUT });
};

export const resetLostPasswordRequest = (): AuthAction => ({type: RESET_LOST_PASSWORD_REQUEST});
export const resetLostPasswordSuccess = (): AuthAction => ({type: RESET_LOST_PASSWORD_SUCCESS});
export const resetLostPasswordFailure = (error: string): AuthAction => ({type: RESET_LOST_PASSWORD_FAILURE, error});

export const updateMyAccountRequest = (): AuthAction => ({ type: UPDATE_MY_ACCOUNT_REQUEST });
export const updateMyAccountSuccess = (userData: any): AuthAction => ({ type: UPDATE_MY_ACCOUNT_SUCCESS, userData });
export const updateMyAccountFailure = (error: string): AuthAction => ({ type: UPDATE_MY_ACCOUNT_FAILURE, error });

export const updateMyAccount = (data: {
  firstName: string;
  lastName: string;
  phone: string;
  gender: string;
  birthdate: string;
  vat?: string;
  // users.language : id de la table languages (langue des e-mails du compte).
  language?: number;
}) => async (dispatch: Dispatch<AuthAction | ReturnType<typeof setNotification>>) => {
  dispatch(updateMyAccountRequest());

  try {
    const response = await api.put('/api/me/account-information', data);

    if (response.status === 200) {
      dispatch(updateMyAccountSuccess(response.data.user));
      dispatch(setNotification({ type: 'success', message: 'Account updated successfully.' }));
    } else {
      throw new Error('Failed to update account.');
    }
  } catch (error: unknown) {
    let errorMessage = 'An error occurred while updating account.';
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      errorMessage = error.response.data.message;
    }
    
    dispatch(updateMyAccountFailure(errorMessage));
    dispatch(setNotification({ type: 'error', message: errorMessage }));
  }
};

export const requestPasswordReset =
  (data: { email: string, language:string }) =>
  async (dispatch: Dispatch<AuthAction | ReturnType<typeof setNotification>>) => {
    dispatch(resetLostPasswordRequest());
    try {
      const response = await api.post(`/api/request-password-reset`, data);

      if (response.status === 200) {
        dispatch(resetLostPasswordSuccess());
        dispatch(
          setNotification({
            type: 'success',
            message: 'If the email exists, a reset link has been sent.',
          })
        );
      } else {
        throw new Error('Password reset request failed.');
      }
    } catch (error: unknown) {
      let errorMessage = 'An unknown error occurred while requesting password reset.';
      if (axios.isAxiosError(error) && error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      dispatch(resetLostPasswordFailure(errorMessage));
      dispatch(
        setNotification({
          type: 'error',
          message: `Failed to request password reset: ${errorMessage}`,
        })
      );
    }
};

export const resetPassword = (email: string, currentPassword: string, newPassword: string, confirmNewPassword: string) => async (dispatch: Dispatch<AuthAction>) => {
  dispatch(resetPasswordRequest());
  try {
    // Replace the URL with your actual reset-password endpoint
    const response = await api.post(`/api/reset-password`, { 
      email, 
      currentPassword, 
      newPassword,
      confirmNewPassword
    });

    // Assuming the API returns a success message or similar
    dispatch(resetPasswordSuccess());
    // Optionally, handle any post-success actions here, such as redirecting the user or showing a success message
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      // Extracting error message from AxiosError
      const message = error.response?.data.message || 'An unknown error occurred during password reset';
      dispatch(resetPasswordFailure(message));
    } else if (error instanceof Error) {
      dispatch(resetPasswordFailure(error.message));
    } else {
      dispatch(resetPasswordFailure('An unknown error occurred during password reset'));
    }
  }
};

export const resetLostPassword =
  (data: { token: string; password: string, language:string  }, onSuccess?: () => void) =>
  async (dispatch: Dispatch<AuthAction>) => {
    dispatch(resetLostPasswordRequest());
    try {
      // API call to reset password using the provided token and new password
      const response = await api.post(`/api/reset-lost-password`, data);

      if (response.status === 200) {
        dispatch(resetLostPasswordSuccess());

        // Optional success callback
        if (onSuccess) onSuccess();
      } else {
        // Handle unexpected response formats
        throw new Error(response.data?.message || 'Password reset failed');
      }
    } catch (error: unknown) {
      let errorMessage = 'An unknown error occurred while resetting the password';

      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data.message || errorMessage;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      dispatch(resetLostPasswordFailure(errorMessage));
    }
  };


/**
 * Complete la ligne d'historique de connexion que le SERVEUR a ecrite (avec
 * l'IP qu'il voit lui-meme) : le navigateur n'ajoute que ce qu'il est seul a
 * connaitre, l'empreinte d'appareil et la taille d'ecran. Plus d'appel a ipify
 * / ipapi : une IP fournie par le navigateur n'est pas fiable, et ces services
 * sont souvent bloques (bloqueurs, CSP, reseaux d'entreprise).
 *
 * Best-effort : ne doit jamais faire echouer la suite de la connexion.
 */
const logUserLogin = async (_userUuid: string) => {
  try {
    let machineId = 'unknown';
    try {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      machineId = result.visitorId || 'unknown';
    } catch {
      // FingerprintJS peut etre bloque, sans consequence.
    }
    const screenResolution =
      typeof window !== 'undefined' && window.screen
        ? `${window.screen.width}x${window.screen.height}`
        : 'unknown';

    // `api` porte le jeton : le compte est lu dans le JWT cote serveur.
    await api.post('/api/user/logUserLogin', { machineId, screenResolution });
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[logUserLogin] best-effort telemetry failed:', err);
    }
  }
};

export const login =
  (email: string, password: string, onSuccess?: (userData: any) => void) =>
  async (dispatch: Dispatch<AuthAction | ReturnType<typeof setNotification>>) => {
  dispatch(loginRequest());
  try {
    const response = await api.post('/api/login', { email, password });

    // Check if 2FA is required
    if (response.data.requiresTwoFactor) {
      dispatch({
        type: LOGIN_2FA_REQUIRED,
        payload: {
          twoFactorToken: response.data.twoFactorToken,
          pendingUser: response.data.user
        }
      });
      return;
    }

    const { user, access_token, refresh_token } = response.data;

    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);

    dispatch(loginSuccess(user));
    if (onSuccess) {
      onSuccess(response.data);
      // Fire-and-forget telemetry — `.catch` here is a belt-and-braces
      // guard in case `logUserLogin` ever loses its internal try/catch
      // again; we never want this to interfere with the post-login
      // flow (cart hydrate, navigation, etc.).
      logUserLogin(user.uuid).catch(() => {});
    }
  } catch (error: unknown) {
    dispatch(setNotification({ type: 'error', message: 'Check your password' }));
    if (error instanceof Error) {
      dispatch(loginFailure(error.message));
    } else {
      dispatch(loginFailure('An unknown error occurred'));
    }
  }
};

export const loginTwoFactor =
  (twoFactorToken: string, code: string, onSuccess?: (userData: any) => void) =>
  async (dispatch: Dispatch<AuthAction | ReturnType<typeof setNotification>>) => {
  dispatch(loginRequest());
  try {
    const response = await axios.post(`${process.env.NEXT_PUBLIC_SOCKET}/api/login/2fa`, {
      twoFactorToken,
      code
    });

    const { user, access_token, refresh_token } = response.data;

    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);

    dispatch(loginSuccess(user));

    if (onSuccess) {
      onSuccess(response.data);
      logUserLogin(user.uuid);
    }
  } catch (error: unknown) {
    dispatch(setNotification({ type: 'error', message: 'Invalid authentication code' }));
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      dispatch(loginFailure(error.response.data.message));
    } else if (error instanceof Error) {
      dispatch(loginFailure(error.message));
    } else {
      dispatch(loginFailure('An unknown error occurred'));
    }
  }
};

export const resetLoginState = (): AuthAction => ({
  type: LOGIN_RESET
});

export const verifyToken = (accessToken: string) => async (dispatch: Dispatch<AuthAction>) => {
  dispatch(verifyTokenRequest());
  try {
      const response = await api.post('/api/verify-token', { data: { access_token: accessToken } });
      if (response.status === 200) {
          dispatch(verifyTokenSuccess(response.data.user));
      } else {
          dispatch(verifyTokenFailure('Token verification failed'));
      }
  } catch (error) {
      console.error('Error verifying token:', error);
      if (axios.isAxiosError(error) && error.response) {
          dispatch(verifyTokenFailure(error.response.data.message || 'Error verifying token'));
      } else {
          dispatch(verifyTokenFailure('An unknown error occurred'));
      }
  }
};

export const register = (data: {
  lastName: string;
  firstName: string;
  gender: string;
  phone: string;
  email: string;
  confirmEmail: string;
  password: string;
  confirmPassword: string;
  city: string;
  bikeTour: string;
  carTour: string;
  language: number;
  spokenLanguages: string[];
  role: number;
  address?: string; // Optional
  country?: string; // Optional
  taxNumber?: string; // Optional
  isPro?: boolean; // Optional
  companyName?: string;
  street?: string;
  streetComplementary?: string;
  cityUser?: string;
  state?: string;
  zip?: string;
}, callback: (userData: any) => void) => async (dispatch: Dispatch<AuthAction | RecruitmentAction  | ReturnType<typeof setNotification>>) => {
  dispatch(registerRequest());
  try {
    // Guard the DOB: an empty / unparseable birthdate reaches the backend
    // as "" and Postgres rejects it ("invalid input syntax for type date").
    // Send null instead so the (nullable) column stores no value.
    const rawBirthdate = (data as any).birthdate;
    const payload = {
      ...data,
      birthdate:
        rawBirthdate && !Number.isNaN(Date.parse(rawBirthdate))
          ? rawBirthdate
          : null,
    };
    const response = await api.post('/api/register', payload);
    let { user, access_token, refresh_token } = response.data;
    console.log('🔵[REG] 1 registered OK. user.role=', user?.role, ' (typeof', typeof user?.role, ') data.role=', (data as any).role, ' (typeof', typeof (data as any).role, ') spokenLanguages=', (data as any).spokenLanguages, ' city=', (data as any).city);

    user = { ...user, access_token };

    dispatch(registerSuccess(user));

// Format final à stocker dans localStorage
const userStorageObject = {
  access_token,
  refresh_token,
  settings: {
    customScrollbars: true,
    theme: {
      footer: "defaultDark",
      main: "defaultDark",
      navbar: "defaultDark",
      toolbar: "defaultDark"
    }
  },
  user,        // ← user plat depuis res.data.user
  data: user   // ← duplicata si besoin
};

// Redux: auth.user = user
dispatch(registerSuccess(user));

// LocalStorage: structure complète avec tokens et duplication
localStorage.setItem('user', JSON.stringify(userStorageObject));
localStorage.setItem('access_token', access_token);
localStorage.setItem('refresh_token', refresh_token);


    // Isolate the caller's callback: if it throws (e.g. a router.push() that
    // errors mid-thunk), it must NOT abort the rest of register() — otherwise
    // the createRecruitment + languageByUser + guideByCity calls below are
    // skipped and guides end up with no recruitment record / no languages /
    // no city.
    try {
      if (callback) callback(user);
    } catch (callbackError) {
      console.error('register: callback threw (non-blocking)', callbackError);
    }

    const lang = localStorage.getItem('lang') || 'en_US';
    let origin = 'from_registration: user';
    if (data.isPro) {
      origin = 'from_registration: pro';
    } else if (user.role === 3) {
      origin = 'from_registration: guide';
    }
    // Newsletter signup is best-effort and FIRE-AND-FORGET. We must NEVER
    // `await` it: the endpoint can HANG (pending forever, "unknown" status)
    // in some environments, and awaiting a never-resolving promise would
    // block register() right here — the critical createRecruitment +
    // languageByUser + guideByCity below would never run, leaving guides
    // with no recruitment record / no languages / no city.
    Promise.resolve(dispatch<any>(addNewsletter(data.email, lang, origin))).catch(
      (newsletterError) =>
        console.error('addNewsletter failed (non-blocking)', newsletterError)
    );

    // Create the recruitment record for guides. Key off the role we SENT
    // (data.role) as well as the one echoed back (user.role): the guide
    // signup always sends role 3, but the backend doesn't always return it
    // as 3 — and without this the record is never created, so the photo /
    // audio uploads later 404 ("No recruitment information found").
    console.log('🔵[REG] 2 reached role check. condition=', user.role === 3 || (data as any).role === 3);
    if (user.role === 3 || (data as any).role === 3) {
      console.log('🔵[REG] 3 ENTERED role-3 block → about to POST createRecruitment');
      // Create the recruitment record. This can come back 500 if a parallel
      // ensureRecruitment already created it (race). Catch it HERE so it does
      // NOT abort the language/city association below — the record exists
      // either way, and those associations are what populate the admin's
      // Languages / City columns.
      try {
        const recruitmentData = { uuid: user.uuid, bikeTour: data.bikeTour, carTour: data.carTour};
        // Jeton joint : la route exige d'etre connecte sur son dossier (audit 2026-10-12).
        const recruitmentToken = localStorage.getItem('access_token');
        const recruitmentResponse = await axios.post(
          `${process.env.NEXT_PUBLIC_SOCKET}/api/createRecruitment`,
          recruitmentData,
          recruitmentToken ? { headers: { Authorization: `Bearer ${recruitmentToken}` } } : {},
        );
        console.log('🔵[REG] 4 createRecruitment OK', recruitmentResponse.status);
        dispatch({ type: CREATE_RECRUITMENT_SUCCESS, payload: recruitmentResponse.data });
      } catch (recruitmentError) {
        console.error('createRecruitment failed (may already exist) — continuing:', recruitmentError);
        dispatch({ type: CREATE_RECRUITMENT_FAILURE, payload: 'Failed to create recruitment' });
      }

      // Associate spoken languages + city. These backend routes REQUIRE the
      // authenticateToken middleware, so they MUST carry the auth header
      // (the middleware just takes Authorization.split(' ')[1], so any
      // scheme works — Bearer/Token alike). Each call is independent + logs
      // its own result so we can see exactly what the server returns.
      console.log('🔵[REG] 5 associating languages/city. spokenLanguages=', data.spokenLanguages, ' city=', data.city);
      const recruitAuth = { headers: { Authorization: `Bearer ${access_token}` } };
      for (const languageId of data.spokenLanguages) {
        try {
          await axios.post(`${process.env.NEXT_PUBLIC_SOCKET}/api/languageByUser`, {
            uuid: user.uuid,
            languageId: parseInt(languageId),
          }, recruitAuth);
          console.log('🔵[REG] 5a languageByUser OK lang=', languageId);
        } catch (e: any) {
          console.error('🔴[REG] languageByUser FAILED lang=', languageId, ' status=', e?.response?.status, ' body=', e?.response?.data);
        }
      }
      try {
        await axios.post(`${process.env.NEXT_PUBLIC_SOCKET}/api/guideByCity`, {
          uuid: user.uuid,
          cityId: data.city,
          activated: false,
          statusUuids: ['standard'],
          carAvailable: data.carTour,
          bikeAvailable: data.bikeTour
        }, recruitAuth);
        console.log('🔵[REG] 5b guideByCity OK city=', data.city);
      } catch (e: any) {
        console.error('🔴[REG] guideByCity FAILED city=', data.city, ' status=', e?.response?.status, ' body=', e?.response?.data);
      }
    }

    if (user.role === 5 && data.isPro) {
      await axios.post(`${process.env.NEXT_PUBLIC_SOCKET}/api/addresses`, {
        userUuid: user.uuid,
        companyName: data.companyName,
        street: data.street,
        streetComplementary: data.streetComplementary,
        city: data.city,
        state: data.state,
        zip: data.zip,
        country: data.country
      }, {
        headers: { Authorization: `Token ${access_token}` }
      });
    }

  } catch (error: unknown) {
    console.error('🔴[REG] CAUGHT ERROR in register main try — aborted here:', error);
    if (axios.isAxiosError(error)) {
      console.log("error during register : ", error)
      const message = error.response?.data.error || 'An unknown error occurred during registration';
      dispatch(registerFailure(message));
      dispatch(setNotification({ type: 'error', message: message }));
    } else if (error instanceof Error) {
      dispatch(registerFailure(error.message));
      dispatch(setNotification({ type: 'error', message: error.message }));
    } else {
      dispatch(registerFailure('An unknown error occurred during registration'));
    }
  }
};

// State type
interface AccountState {
    loading: boolean;
    user: any; // Replace 'any' with your user data type
    error: string | null;
    requiresTwoFactor?: boolean;
    twoFactorToken?: string;
    pendingUser?: any;
}



  // Initial state
  const initialState: AccountState = {
    loading: false,
    user: null,
    error: null,
    requiresTwoFactor: false,
    twoFactorToken: undefined,
    pendingUser: undefined,
  };

// Reducer
const accountReducer = (state: AccountState = initialState, action: AuthAction): AccountState => {
  switch (action.type) {
    case LOGIN_REQUEST:
      return { ...state, loading: true, error: null };
    case LOGIN_SUCCESS:
      return { ...state, loading: false, user: action.userData, error: null, requiresTwoFactor: false, twoFactorToken: undefined, pendingUser: undefined };
    case LOGIN_FAILURE:
      return { ...state, loading: false, error: action.error };
    case LOGIN_2FA_REQUIRED:
      return {
        ...state,
        loading: false,
        requiresTwoFactor: true,
        twoFactorToken: action.payload.twoFactorToken,
        pendingUser: action.payload.pendingUser,
        error: null
      };
    case LOGIN_RESET:
      // Clear only the *transient* login state (loading flag, error
      // message, 2FA bookkeeping). Preserving `user` is critical: the
      // login modal dispatches this action when it unmounts after a
      // successful login (to wipe any stale error), and clobbering
      // `user` here logs the freshly-authenticated session straight
      // back out — header keeps showing "Connexion", the avatar
      // dropdown never appears, etc.
      return {
        ...state,
        loading: false,
        error: null,
        requiresTwoFactor: false,
        twoFactorToken: undefined,
        pendingUser: undefined,
      };
    case LOGOUT:
      return {
        ...state,
        user: null,
        error: null,
        loading: false,
        requiresTwoFactor: false,
        twoFactorToken: undefined,
        pendingUser: undefined,
      };
    case VERIFY_TOKEN_REQUEST:
      return { ...state, loading: true };
    case VERIFY_TOKEN_SUCCESS:
      return { ...state, loading: false, user: action.userData, error: null };
    case VERIFY_TOKEN_FAILURE:
      return { ...state, loading: false, error: action.error };
    case REGISTER_REQUEST:
        return { ...state, loading: true, error: null };
    case REGISTER_SUCCESS:
        return { ...state, loading: false, user: action.userData, error: null };
    case REGISTER_FAILURE:
        return { ...state, loading: false, error: action.error };
    case CREATE_RECRUITMENT_SUCCESS:
      // Handle recruitment creation success, if needed
      return { ...state };
    case CREATE_RECRUITMENT_FAILURE:
      // Optionally handle recruitment creation failure
      return { ...state, error: action.payload };
    case RESET_PASSWORD_REQUEST:
      return { ...state, loading: true, error: null };
    case RESET_PASSWORD_SUCCESS:
      return { ...state, loading: false, error: null }; // You might want to update the state in a specific way on success
    case RESET_PASSWORD_FAILURE:
      return { ...state, loading: false, error: action.error };
    case RESET_LOST_PASSWORD_REQUEST:
      return { ...state, loading: true, error: null};
    case RESET_LOST_PASSWORD_SUCCESS:
      return { ...state, loading: false, error: null};
    case RESET_LOST_PASSWORD_FAILURE:
      return { ...state, loading: false, error: action.error};
    case REMOVE_AUTH_ERROR:
      return { ...state, loading: false, error: null };
    case UPDATE_MY_ACCOUNT_REQUEST:
      return { ...state, loading: true, error: null };
    case UPDATE_MY_ACCOUNT_SUCCESS:
      // Fusion et non remplacement : la reponse ne porte que ce que l'ecran a
      // modifie. L'ecraser entierement faisait perdre a la session tout ce qui
      // n'y figure pas (isPro, isHeadOffice, role, email) et les entrees de
      // menu correspondantes disparaissaient jusqu'au rechargement suivant.
      // Le serveur renvoie desormais le compte complet ; la fusion reste le
      // filet, pour que ce bug ne puisse pas revenir par une autre reponse
      // partielle.
      return {
        ...state,
        loading: false,
        user: { ...(state.user || {}), ...(action.userData || {}) },
        error: null,
      };
    case UPDATE_MY_ACCOUNT_FAILURE:
      return { ...state, loading: false, error: action.error };
    default:
      return state;
  }
};

export default accountReducer;
